// WorkflowAuditHistory — A-101b: extraherat ur ShareWorkspaceDialog.
//
// Collapsible som visar insert/update/delete-händelser för en workflow
// med action-filter + paginerad listning. Bor i egen fil för tydlig
// separation och enklare isolerad testning.

import { useState } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { History, ChevronDown, ChevronRight } from 'lucide-react';
import { useWorkflowAuditLog } from '@/hooks/useWorkflowAuditLog';
import { languageToLocale } from '@/lib/locale';
import { formatAuditSummary } from '@/lib/formatAuditSummary';
import { useTranslation } from '@/i18n/LanguageContext';

type ActionFilter = 'all' | 'insert' | 'update' | 'delete';

export interface WorkflowAuditHistoryProps {
  /** Workflow-id att hämta audit-log för. `null` → hook:en hämtar inget. */
  workspaceId: string | null;
  /** Initial-open-state. Default false (collapsed). */
  defaultOpen?: boolean;
}

export function WorkflowAuditHistory({
  workspaceId, defaultOpen = false,
}: WorkflowAuditHistoryProps) {
  const { t, language } = useTranslation();
  const localeTag = languageToLocale(language);

  const [open, setOpen] = useState(defaultOpen);
  const [actionFilter, setActionFilter] = useState<ActionFilter>('all');
  const [auditVisible, setAuditVisible] = useState(20);

  // Hooken hämtar bara data när både workspaceId och open är truthy.
  const audit = useWorkflowAuditLog(open ? workspaceId : null);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground w-full"
        data-testid="audit-history-trigger"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <History className="w-3.5 h-3.5" />
        {t('auditHistory')}
        {audit.entries.length > 0 && (
          <span className="ml-auto text-[10px] bg-muted px-1.5 py-0.5 rounded">
            {audit.entries.length}
          </span>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 space-y-2">
        <div className="flex gap-1">
          {(['all', 'insert', 'update', 'delete'] as const).map((a) => (
            <button
              key={a}
              onClick={() => setActionFilter(a)}
              className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                actionFilter === a
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted'
              }`}
            >
              {a}
            </button>
          ))}
        </div>
        {audit.loading ? (
          <p className="text-xs text-muted-foreground py-2">…</p>
        ) : audit.entries.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">{t('auditEmpty')}</p>
        ) : (() => {
          const filtered = actionFilter === 'all'
            ? audit.entries
            : audit.entries.filter((e) => e.action === actionFilter);
          const visible = filtered.slice(0, auditVisible);
          return (
            <>
              {/* Q-103: mobile-responsive sizing */}
              <ul className="space-y-1 max-h-64 sm:max-h-48 overflow-y-auto text-[12px] sm:text-[11px]">
                {visible.map((e) => (
                  <li
                    key={e.id}
                    className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-2 py-1 border-b border-border/40 last:border-0"
                  >
                    <span className="text-muted-foreground whitespace-nowrap text-[10px] sm:text-[11px]">
                      {new Date(e.created_at).toLocaleString(localeTag, {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </span>
                    <span className="font-medium truncate flex-1" title={e.summary ?? ''}>
                      {formatAuditSummary(e.summary, t) || e.action}
                    </span>
                    <span
                      className="text-muted-foreground truncate max-w-[180px] sm:max-w-[120px]"
                      title={e.actor_id ?? ''}
                    >
                      {e.actor_redacted
                        ? t('auditRedactedActor')
                        : (e.actor_id && audit.actorEmails[e.actor_id]) || '—'}
                    </span>
                  </li>
                ))}
              </ul>
              {visible.length < filtered.length && (
                <button
                  onClick={() => setAuditVisible((v) => v + 20)}
                  className="text-[10px] text-primary hover:underline w-full text-center py-1"
                >
                  Ladda fler ({filtered.length - visible.length} kvar)
                </button>
              )}
            </>
          );
        })()}
      </CollapsibleContent>
    </Collapsible>
  );
}
